import { useMutation, useQuery } from '@apollo/client';

import { BugTracker } from 'kb-shared/utilities/bugTracker';

import {
  MEDICAL_RECORDS_ANNOUNCEMENT_SETTINGS,
  DISMISS_MEDICAL_RECORDS_ANNOUNCEMENT
} from './MedicalRecordsAnnouncement.graphql';

interface AnnouncementSettings {
  id: string;
  medicalRecordsAnnouncementDismissed: boolean;
}

interface AnnouncementSettingsResponse {
  patientUiSettings: AnnouncementSettings | null;
}

interface DismissAnnouncementResponse {
  dismissMedicalRecordsAnnouncement: {
    patientUiSettings: AnnouncementSettings;
  };
}

export const useMedicalRecordsAnnouncement = () => {
  const { data, loading, error } = useQuery<AnnouncementSettingsResponse>(
    MEDICAL_RECORDS_ANNOUNCEMENT_SETTINGS,
    {
      fetchPolicy: 'network-only',
      onError: error => BugTracker.notify(error, 'Load medical records announcement preference')
    }
  );
  const [dismissAnnouncement, { loading: dismissing, error: dismissError }] =
    useMutation<DismissAnnouncementResponse>(DISMISS_MEDICAL_RECORDS_ANNOUNCEMENT, {
      onError: error => BugTracker.notify(error, 'Dismiss medical records announcement'),
      update: (cache, { data }) => {
        if (!data) return;

        cache.writeQuery<AnnouncementSettingsResponse>({
          query: MEDICAL_RECORDS_ANNOUNCEMENT_SETTINGS,
          data: { patientUiSettings: data.dismissMedicalRecordsAnnouncement.patientUiSettings }
        });
      }
    });

  const dismiss = async () => {
    const response = await dismissAnnouncement();
    return (
      response.data?.dismissMedicalRecordsAnnouncement.patientUiSettings
        .medicalRecordsAnnouncementDismissed === true
    );
  };

  return {
    visible:
      Boolean(data) &&
      !loading &&
      !error &&
      !data?.patientUiSettings?.medicalRecordsAnnouncementDismissed,
    dismiss,
    dismissing,
    dismissError
  };
};
